import Heap from 'heap';
import { invariant } from '../util/invariant';
import type { BlobView} from './blobView';
import { BufferBlob, Offset } from './blobView';

/**
 * A description of a range of bytes written to a virtual output byte sequence.
 * Byte ranges are used both for describing write of large chunks of data, as
 * well as patches to already written data. As such byte ranges can overlap
 * until they have been resolved into a linear sequence of byte ranges.
 */
interface ByteRange {
  position: number;
  byteCount: number;
  priority: number;
  blob: BlobView;
}

/**
 * When travering byte ranges to produce a flat list of non-overlapping ranges,
 * we look at the available byte ranges in order of output position.
 */
function orderByteRange(a: ByteRange, b: ByteRange) {
  return a.position - b.position;
}

/**
 * Create an ArrayBuffer containing an unsigned integer big-endian encoded.
 */
function makeBigEndianUnsignedIntegerConstantBuffer(
  byteCount: number,
  value: bigint
): ArrayBuffer {
  const buf = new Uint8Array(byteCount);
  for (let i = 0; i < byteCount; ++i) {
    buf[i] = Number(
      // eslint-disable-next-line no-bitwise
      BigInt.asUintN(8, value >> BigInt((byteCount - 1 - i) * 8))
    );
  }
  return buf.buffer;
}

/**
 * A blob writer that defers actual writing of bytes by keeping an internal list
 * of byte ranges that have been written and patched.
 *
 * This allows us to construct the output the output by means of writes and
 * back-patching, while not requiring the output stream to support seeking.
 *
 * It also allows us to construct a browser blob consisting of the rearranged
 * contents of other browser blobs, without ever needing to load all the bytes
 * of the other browser blobs into JavaScript memory.
 */
export class BlobWriter {
  private pos = 0;
  priority = 0;

  lastBlobOffset: Offset | null = null;
  lastPosition: number | null = null;
  ranges: ByteRange[] = [];
  patches: ByteRange[] = [];

  /**
   * Write a blob after previously written blobs.
   */
  async write(blob: BlobView, size: number) {
    if (this.ranges.length > 0) {
      const last = this.ranges[this.ranges.length - 1]!;
      const extended = last.blob.tryExtend(blob);
      if (extended != null) {
        last.byteCount += size;
        last.blob = extended;
        this.pos += size;
        return;
      }
    }

    const range: ByteRange = {
      position: this.pos,
      byteCount: size,
      priority: this.priority++,
      blob,
    };

    this.ranges.push(range);
    this.lastBlobOffset = blob.offset();
    this.lastPosition = this.pos;
    this.pos += size;
  }

  /**
   * Patch data previously written.
   */
  async patch(offset: Offset, bytes: ArrayBuffer) {
    invariant(
      this.lastPosition != null && this.lastBlobOffset != null,
      'Cannot patch content not yet written'
    );
    invariant(
      this.lastBlobOffset != null,
      'Cannot position patch relative an anonymous blob'
    );
    invariant(offset.id == this.lastBlobOffset.id, 'Unknown patch offset');
    const lastWriteOffset = offset.position - this.lastBlobOffset.position;
    const position = this.lastPosition + lastWriteOffset;

    this.patches.push({
      position,
      byteCount: bytes.byteLength,
      priority: this.priority++,
      blob: new BufferBlob(bytes, new Offset(null, 0)),
    });
  }

  /**
   * Patch a big-endian unsigned integer residing somewhere in previously
   * written data.
   */
  async patchUInt(offset: Offset, byteCount: number, value: bigint) {
    await this.patch(
      offset,
      makeBigEndianUnsignedIntegerConstantBuffer(byteCount, value)
    );
  }

  /**
   * Get a flat list of blobs that when concatenated results in the full
   * sequence of bytes written using this blob writer.
   */
  getBlobSequence(): BlobView[] {
    const out: ByteRange[] = [];
    const ranges = new Heap(orderByteRange);
    for (const range of this.ranges) {
      ranges.push(range);
    }
    for (const range of this.patches) {
      ranges.push(range);
    }
    let current = ranges.pop();
    let pos = 0;
    while (current != null) {
      const next = ranges.top();
      if (!next) {
        // No more ranges to be concerned about. Simply write out current.
        invariant(current.position == pos, 'Wrong position');
        pos += current.byteCount;
        out.push(current);
        current = ranges.pop();
        continue;
      }

      if (current.position + current.byteCount <= next.position) {
        // Next range does not overlap with current. Simply write out current.
        invariant(current.position == pos, 'Wrong position');
        pos += current.byteCount;
        out.push(current);
        current = ranges.pop();
        continue;
      }

      if (current.priority > next.priority) {
        /*
          Current has greater priority than overlapping next. Trim next to
          remove overlapping part.
        */
        const poppedNext = ranges.pop()!;
        const currentEnd = current.position + current.byteCount;
        const overlap = currentEnd - next.position;
        poppedNext.blob = poppedNext.blob.slice(overlap);
        invariant(
          poppedNext.position + overlap == currentEnd,
          'Unexpected position'
        );
        poppedNext.position = currentEnd;
        poppedNext.byteCount -= overlap;
        if (poppedNext.byteCount > 0) {
          // Reinsert if trimmed range still has bytes.
          ranges.push(poppedNext);
        }
        continue;
      }

      const uncovered = next.position - current.position;
      invariant(uncovered >= 0, 'Did not expect to go backwards');
      invariant(current.position == pos, 'Wrong position');
      if (uncovered > 0) {
        pos += uncovered;
        out.push({
          position: current.position,
          byteCount: uncovered,
          priority: current.priority,
          blob: current.blob.slice(0, uncovered),
        });
      }

      const discard = uncovered + next.byteCount;
      const nextEnd = next.position + next.byteCount;
      invariant(nextEnd - current.position == discard, 'Bad discard');
      current.position += discard;
      current.byteCount -= discard;
      current.blob = current.blob.slice(discard);
      if (current.byteCount > 0) {
        ranges.push(current);
      }

      current = ranges.pop();
      continue;
    }
    return out.map((range) => range.blob);
  }
}
